import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Building2, Home, ArrowRight, Palette, Cpu, Award, Users, Briefcase, FileText, Key, Scale, Rocket, Globe, Lightbulb, FileCheck, Gavel, Search, Brain, Bot, ShieldCheck } from 'lucide-react';

const Services = () => {
  const { t, language } = useLanguage();
  const [activeService, setActiveService] = useState(0);

  // Translations for subcategories
  const subcategoryTranslations = {
    en: {
      ip: [
        { icon: Palette, name: 'Art Law', desc: 'Protection of artistic works and cultural assets' },
        { icon: Cpu, name: 'Technology Law', desc: 'Software patents, digital rights, tech licensing' },
        { icon: Lightbulb, name: 'Patent Law', desc: 'Patent applications, protection and enforcement' },
      ],
      trademark: [
        { icon: Award, name: 'Trademark Registration', desc: 'National and international trademark filing' },
        { icon: Search, name: 'Trademark Search', desc: 'Availability searches and clearance opinions' },
        { icon: Gavel, name: 'Trademark Litigation', desc: 'Opposition, cancellation and infringement cases' },
      ],
      corporate: [
        { icon: Users, name: 'Corporate Services', desc: 'Consultancy, contracts, corporate governance' },
        { icon: Briefcase, name: 'Corporate Projects', desc: 'M&A, Liquidation, Due Diligence, Formation' },
      ],
      realEstate: [
        { icon: Key, name: 'Acquisition', desc: 'Property purchase including Due Diligence' },
        { icon: FileText, name: 'Lease & Rent', desc: 'Commercial and residential lease agreements' },
        { icon: Scale, name: 'Real Estate Disputes', desc: 'Ownership issues and property litigation' },
      ],
      startup: [
        { icon: Rocket, name: 'Company Formation', desc: 'Legal structuring and incorporation for startups' },
        { icon: FileCheck, name: 'Investment Agreements', desc: 'SAFE, convertible notes, term sheets' },
        { icon: Globe, name: 'Scaling & Compliance', desc: 'Growth-stage legal support and regulatory compliance' },
      ],
      ai: [
        { icon: Brain, name: 'AI Governance', desc: 'Compliance with AI regulations and ethical frameworks' },
        { icon: Bot, name: 'AI Contracts', desc: 'Licensing, development agreements, and liability' },
        { icon: ShieldCheck, name: 'Data Protection', desc: 'GDPR, KVKK compliance for AI systems' },
      ],
      getConsultation: 'Get consultation',
      sectionLabel: 'What We Do',
    },
    tr: {
      ip: [
        { icon: Palette, name: 'Kültür ve Sanat Hukuku', desc: 'Sanat eserleri ve kültürel varlıkların korunması' },
        { icon: Cpu, name: 'Bilişim Hukuku', desc: 'Yazılım, dijital haklar, teknoloji lisansları' },
        { icon: Lightbulb, name: 'Patent Hukuku', desc: 'Patent başvuruları, koruma ve ihlal davaları' },
      ],
      trademark: [
        { icon: Award, name: 'Marka Tescili', desc: 'Ulusal ve uluslararası marka başvuruları' },
        { icon: Search, name: 'Marka Araştırması', desc: 'Benzerlik araştırması ve ön değerlendirme' },
        { icon: Gavel, name: 'Marka Davaları', desc: 'İtiraz, hükümsüzlük ve tecavüz davaları' },
      ],
      corporate: [
        { icon: Users, name: 'Şirket Danışmanlığı', desc: 'Sözleşmeler, kurumsal yönetim, uyum danışmanlığı' },
        { icon: Briefcase, name: 'Şirket İşlemleri', desc: 'Birleşme ve devralma, tasfiye, yapılandırma' },
      ],
      realEstate: [
        { icon: Key, name: 'Taşınmaz Alım-Satım', desc: 'Hukuki inceleme dahil gayrimenkul edinimi' },
        { icon: FileText, name: 'Kira Hukuku', desc: 'Ticari ve konut kira sözleşmeleri' },
        { icon: Scale, name: 'Gayrimenkul Davaları', desc: 'Tapu iptali, el atma, kat mülkiyeti uyuşmazlıkları' },
      ],
      startup: [
        { icon: Rocket, name: 'Şirket Kuruluşu', desc: 'Start-up\'lar için hukuki yapılandırma ve kuruluş' },
        { icon: FileCheck, name: 'Yatırım Sözleşmeleri', desc: 'SAFE, dönüştürülebilir tahvil, term sheet' },
        { icon: Globe, name: 'Büyüme ve Uyum', desc: 'Büyüme aşaması hukuki destek ve mevzuat uyumu' },
      ],
      ai: [
        { icon: Brain, name: 'Yapay Zeka Yönetişimi', desc: 'Yapay zeka düzenlemeleri ve etik çerçevelere uyum' },
        { icon: Bot, name: 'Yapay Zeka Sözleşmeleri', desc: 'Lisanslama, geliştirme sözleşmeleri ve sorumluluk' },
        { icon: ShieldCheck, name: 'Veri Koruma', desc: 'Yapay zeka sistemleri için KVKK ve GDPR uyumu' },
      ],
      getConsultation: 'Detaylı bilgi alın',
      sectionLabel: 'Faaliyet Alanlarımız',
    },
    es: {
      ip: [
        { icon: Palette, name: 'Derecho del Arte', desc: 'Protección de obras artísticas y activos culturales' },
        { icon: Cpu, name: 'Derecho Tecnológico', desc: 'Patentes de software, derechos digitales, licencias tecnológicas' },
        { icon: Lightbulb, name: 'Derecho de Patentes', desc: 'Solicitudes de patentes, protección y aplicación' },
      ],
      trademark: [
        { icon: Award, name: 'Registro de Marcas', desc: 'Registro nacional e internacional de marcas' },
        { icon: Search, name: 'Búsqueda de Marcas', desc: 'Búsquedas de disponibilidad y opiniones de viabilidad' },
        { icon: Gavel, name: 'Litigios de Marcas', desc: 'Oposición, cancelación y casos de infracción' },
      ],
      corporate: [
        { icon: Users, name: 'Servicios Societarios', desc: 'Consultoría, contratos, gobierno corporativo' },
        { icon: Briefcase, name: 'Proyectos Societarios', desc: 'Fusiones y Adquisiciones, Liquidación, Due Diligence, Constitución' },
      ],
      realEstate: [
        { icon: Key, name: 'Adquisición', desc: 'Compra de inmuebles incluyendo Due Diligence' },
        { icon: FileText, name: 'Arrendamiento', desc: 'Contratos de arrendamiento mercantil y residencial' },
        { icon: Scale, name: 'Litigios Inmobiliarios', desc: 'Conflictos de propiedad y litigios inmobiliarios' },
      ],
      startup: [
        { icon: Rocket, name: 'Constitución de Empresas', desc: 'Estructuración legal e incorporación para startups' },
        { icon: FileCheck, name: 'Acuerdos de Inversión', desc: 'SAFE, notas convertibles, term sheets' },
        { icon: Globe, name: 'Escalamiento y Cumplimiento', desc: 'Soporte legal en etapa de crecimiento y cumplimiento normativo' },
      ],
      ai: [
        { icon: Brain, name: 'Gobernanza de IA', desc: 'Cumplimiento de regulaciones de IA y marcos éticos' },
        { icon: Bot, name: 'Contratos de IA', desc: 'Licencias, acuerdos de desarrollo y responsabilidad' },
        { icon: ShieldCheck, name: 'Protección de Datos', desc: 'Cumplimiento RGPD para sistemas de IA' },
      ],
      getConsultation: 'Solicitar consulta',
      sectionLabel: 'Nuestros Servicios',
    },
    fr: {
      ip: [
        { icon: Palette, name: 'Droit de l\'Art', desc: 'Protection des œuvres artistiques et des biens culturels' },
        { icon: Cpu, name: 'Droit du Numérique', desc: 'Brevets logiciels, droits numériques, licences technologiques' },
        { icon: Lightbulb, name: 'Droit des Brevets', desc: 'Demandes de brevets, protection et application' },
      ],
      trademark: [
        { icon: Award, name: 'Enregistrement de Marques', desc: 'Dépôt de marques national et international' },
        { icon: Search, name: 'Recherche de Marques', desc: 'Recherches de disponibilité et avis de faisabilité' },
        { icon: Gavel, name: 'Contentieux des Marques', desc: 'Opposition, annulation et cas de contrefaçon' },
      ],
      corporate: [
        { icon: Users, name: 'Services aux Sociétés', desc: 'Conseil, contrats, gouvernance d\'entreprise' },
        { icon: Briefcase, name: 'Opérations Sociétaires', desc: 'Fusions et Acquisitions, Liquidation, Due Diligence, Constitution' },
      ],
      realEstate: [
        { icon: Key, name: 'Acquisition', desc: 'Achat immobilier incluant Due Diligence' },
        { icon: FileText, name: 'Baux', desc: 'Baux commerciaux et résidentiels' },
        { icon: Scale, name: 'Contentieux Immobilier', desc: 'Questions de propriété et litiges immobiliers' },
      ],
      startup: [
        { icon: Rocket, name: 'Constitution de Sociétés', desc: 'Structuration juridique et création pour startups' },
        { icon: FileCheck, name: 'Accords d\'Investissement', desc: 'SAFE, obligations convertibles, term sheets' },
        { icon: Globe, name: 'Croissance et Conformité', desc: 'Support juridique en phase de croissance et conformité réglementaire' },
      ],
      ai: [
        { icon: Brain, name: 'Gouvernance de l\'IA', desc: 'Conformité aux réglementations IA et cadres éthiques' },
        { icon: Bot, name: 'Contrats IA', desc: 'Licences, accords de développement et responsabilité' },
        { icon: ShieldCheck, name: 'Protection des Données', desc: 'Conformité RGPD pour les systèmes d\'IA' },
      ],
      getConsultation: 'Demander une consultation',
      sectionLabel: 'Nos Services',
    },
  };

  const currentTranslations = subcategoryTranslations[language] || subcategoryTranslations.en;

  const servicesData = [
    {
      id: 1,
      icon: Shield,
      color: 'blue',
      title: t('services.items')?.[0]?.title || 'Intellectual Property Law',
      description: t('services.items')?.[0]?.description || 'Protecting your creative assets and innovations with expert IP counsel.',
      subcategories: currentTranslations.ip
    },
    {
      id: 2,
      icon: Award,
      color: 'amber',
      title: t('services.items')?.[1]?.title || 'Trademark Law',
      description: t('services.items')?.[1]?.description || 'Comprehensive trademark services from registration to enforcement.',
      subcategories: currentTranslations.trademark
    },
    {
      id: 3,
      icon: Building2,
      color: 'purple',
      title: t('services.items')?.[2]?.title || 'Corporate Law',
      description: t('services.items')?.[2]?.description || 'Strategic legal counsel for businesses operating in Turkey.',
      subcategories: currentTranslations.corporate
    },
    {
      id: 4,
      icon: Home,
      color: 'green',
      title: t('services.items')?.[3]?.title || 'Real Estate Law',
      description: t('services.items')?.[3]?.description || 'Expert guidance through Turkey\'s real estate market.',
      subcategories: currentTranslations.realEstate
    },
    {
      id: 5,
      icon: Rocket,
      color: 'orange',
      title: t('services.items')?.[4]?.title || 'Start-up Law',
      description: t('services.items')?.[4]?.description || 'Legal support for startups from formation to growth stage.',
      subcategories: currentTranslations.startup
    },
    {
      id: 6,
      icon: Brain,
      color: 'cyan',
      title: t('services.items')?.[5]?.title || 'AI Law',
      description: t('services.items')?.[5]?.description || 'Legal guidance for AI development, deployment and compliance.',
      subcategories: currentTranslations.ai
    }
  ];

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      bgHover: 'group-hover:bg-blue-100',
      icon: 'text-blue-600',
      border: 'border-blue-200',
      accent: 'bg-blue-600',
    },
    amber: {
      bg: 'bg-amber-50',
      bgHover: 'group-hover:bg-amber-100',
      icon: 'text-amber-600',
      border: 'border-amber-200',
      accent: 'bg-amber-600',
    },
    purple: {
      bg: 'bg-purple-50',
      bgHover: 'group-hover:bg-purple-100',
      icon: 'text-purple-600',
      border: 'border-purple-200',
      accent: 'bg-purple-600',
    },
    green: {
      bg: 'bg-green-50',
      bgHover: 'group-hover:bg-green-100',
      icon: 'text-green-600',
      border: 'border-green-200',
      accent: 'bg-green-600',
    },
    orange: {
      bg: 'bg-orange-50',
      bgHover: 'group-hover:bg-orange-100',
      icon: 'text-orange-600',
      border: 'border-orange-200',
      accent: 'bg-orange-600',
    },
    cyan: {
      bg: 'bg-cyan-50',
      bgHover: 'group-hover:bg-cyan-100',
      icon: 'text-cyan-600',
      border: 'border-cyan-200',
      accent: 'bg-cyan-600',
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="relative bg-white py-24 lg:py-32">
      {/* Background Image Accent */}
      <div className="absolute top-0 right-0 w-1/3 h-96 opacity-[0.03]">
        <img 
          src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&auto=format&fit=crop&q=60"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#1e3a5f] font-semibold text-sm uppercase tracking-wider">{currentTranslations.sectionLabel}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mt-4 mb-6" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
            {t('services.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {servicesData.map((service, index) => {
            const IconComponent = service.icon;
            const colors = colorClasses[service.color];
            const isActive = activeService === index;

            return (
              <div
                key={service.id}
                className={`group bg-white rounded-2xl p-8 border-2 transition-all duration-300 cursor-pointer ${
                  isActive ? `${colors.border} shadow-xl` : 'border-gray-100 hover:border-gray-200 hover:shadow-lg'
                }`}
                onMouseEnter={() => setActiveService(index)}
              >
                {/* Icon */}
                <div className={`w-16 h-16 ${colors.bg} ${colors.bgHover} rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300`}>
                  <IconComponent className={`w-8 h-8 ${colors.icon}`} />
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                  {service.description}
                </p>

                {/* Subcategories */}
                <div className="space-y-4 mb-6">
                  {service.subcategories.map((sub, idx) => {
                    const SubIcon = sub.icon;
                    return (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                        <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <SubIcon className={`w-4 h-4 ${colors.icon}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-[#1e3a5f] text-sm">{sub.name}</p>
                          <p className="text-gray-500 text-xs">{sub.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* CTA */}
                <button
                  onClick={scrollToContact}
                  className={`flex items-center gap-2 ${colors.icon} font-medium group-hover:gap-3 transition-all text-sm`}
                >
                  {currentTranslations.getConsultation}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
